import { useMemo } from "react";
import { z } from "zod";
import {
  BeaconFundRequest,
  BeaconAllocation,
  BeaconSummary,
  useListBeaconSummary,
  useListBeaconRequests,
  useListBeaconAllocations,
  ListBeaconFiltersValidation,
} from "@/lib/api/fund";
import { useListMinimalUsers } from "@/lib/api/users";
import { useListCustomers } from "@/lib/api/customer";

export function useBeaconData(filters: z.infer<typeof ListBeaconFiltersValidation>) {
  // Load users and customers
  const minimalUserListResult = useListMinimalUsers();
  const users = (minimalUserListResult?.data?.data || []);
  const customersResult = useListCustomers({ minimal: true });
  const customers = (customersResult?.data?.data || []);

  // Load data
  const summaryResult = useListBeaconSummary({ filters });
  const fundRequestsResult = useListBeaconRequests({ filters });
  const allocationsResult = useListBeaconAllocations({ filters });
  
  const fundRequests: BeaconFundRequest[] = (fundRequestsResult?.data?.data && typeof fundRequestsResult.data?.data === 'object' && 'fund_requests' in fundRequestsResult.data?.data)
    ? (fundRequestsResult.data?.data as any).fund_requests
    : [];
  const allocations: BeaconAllocation[] = (allocationsResult?.data?.data && typeof allocationsResult.data?.data === 'object' && 'allocations' in allocationsResult.data?.data)
    ? (allocationsResult.data?.data as any).allocations
    : [];

  // Use summary data for stats
  const {
    summary: yearStats,
    totalSummary: allStats,
  } = useMemo(() => {
    const summary: BeaconSummary | null = (summaryResult?.data?.data && typeof summaryResult.data?.data === 'object' && 'summary' in summaryResult.data?.data)
      ? (summaryResult.data?.data as any).summary
      : null;
    const totalSummary: BeaconSummary | null = (summaryResult?.data?.data && typeof summaryResult.data?.data === 'object' && 'total_summary' in summaryResult.data?.data)
      ? (summaryResult.data?.data as any).total_summary
      : null;
    const calculateSummaryFor = (summary: any) => {
      if (!summary) {
        return {
          employeeTotal: 0,
          customerTotal: 0,
          employeeMatched: 0,
          customerMatched: 0,
          totalAllocated: 0,
          totalRequested: 0,
          totalMatched: 0,
          totalDisbursed: 0,
        };
      }

      return {
        employeeTotal: parseFloat(summary.employee_stats.total_requested || "0"),
        customerTotal: parseFloat(summary.customer_stats.total_requested || "0"),
        employeeMatched: parseFloat(summary.employee_stats.total_matched || "0"),
        customerMatched: parseFloat(summary.customer_stats.total_matched || "0"),
        totalAllocated: parseFloat(summary.total_allocated || "0"),
        totalRequested: parseFloat(summary.total_requested || "0"),
        totalMatched: parseFloat(summary.total_matched || "0"),
        totalDisbursed: parseFloat(summary.total_disbursed || "0"),
      }
    };

    return {
      summary: calculateSummaryFor(summary),
      totalSummary: calculateSummaryFor(totalSummary),
    }
  }, [summaryResult?.data?.data || {}]);

  // Check for loading and error states
  const isLoading = summaryResult.isLoading || fundRequestsResult.isLoading || allocationsResult.isLoading;
  const hasError = summaryResult.error || fundRequestsResult.error || allocationsResult.error;

  // Individual loading states
  const isSummaryLoading = summaryResult.isLoading;
  const isFundRequestsLoading = fundRequestsResult.isLoading;
  const isAllocationsLoading = allocationsResult.isLoading;

  return {
    users,
    customers,
    fundRequests,
    allocations,
    yearStats,
    allStats,
    isLoading,
    hasError,
    isSummaryLoading,
    isFundRequestsLoading,
    isAllocationsLoading,
    minimalUserListResult,
    customersResult,
  };
}